import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Send, Loader2, CheckCircle } from 'lucide-react';

const API = '/api';

export default function SendDocument() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [signers, setSigners] = useState([{ name: '', email: '', signing_order: 0 }]);
  const [message, setMessage] = useState('');
  const [sequential, setSequential] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get(`${API}/documents/${docId}`)
      .then(r => {
        setDoc(r.data);
        // Pre-populate signer count based on fields
        const uniqueSigners = new Set((r.data.fields || []).map(f => f.signer_index));
        if (uniqueSigners.size > 1) {
          setSigners(
            Array.from({ length: uniqueSigners.size }, (_, i) => ({
              name: '', email: '', signing_order: i,
            }))
          );
        }
      })
      .catch(() => {});
  }, [docId]);

  const addSigner = () => {
    setSigners(s => [...s, { name: '', email: '', signing_order: s.length }]);
  };

  const updateSigner = (i, field, value) => {
    setSigners(s => s.map((x, idx) => idx === i ? { ...x, [field]: value } : x));
  };

  const removeSigner = (i) => {
    if (signers.length <= 1) return;
    setSigners(s => s.filter((_, idx) => idx !== i).map((x, idx) => ({ ...x, signing_order: idx })));
  };

  const send = async () => {
    for (const s of signers) {
      if (!s.name.trim() || !s.email.trim()) {
        return setError('Please fill in all signer name and email fields.');
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email)) {
        return setError(`Invalid email: ${s.email}`);
      }
    }
    setSending(true);
    setError('');
    try {
      await axios.post(`${API}/documents/${docId}/send`, {
        signers: signers.map((s, i) => ({ ...s, signing_order: i })),
        message: message.trim() || null,
        sequential,
      });
      setSuccess(true);
      setTimeout(() => navigate('/esign'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send');
      setSending(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Document Sent!</h1>
        <p className="text-gray-500">
          Signing invitations have been emailed to {signers.length} signer{signers.length > 1 ? 's' : ''}.
          Redirecting...
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <Link to={`/esign/editor/${docId}`} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-6 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to Editor
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Send for Signing</h1>
      {doc && <p className="text-gray-500 mb-6 text-sm">{doc.filename}</p>}

      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
      )}

      {/* Signers */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
        <h2 className="font-semibold text-gray-800 mb-4">Signers</h2>
        <div className="space-y-3">
          {signers.map((signer, i) => (
            <div key={i} className="flex gap-3 items-start">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold mt-2">
                {i + 1}
              </div>
              <div className="flex-1 grid grid-cols-2 gap-2">
                <input
                  placeholder="Full Name"
                  value={signer.name}
                  onChange={e => updateSigner(i, 'name', e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="email"
                  placeholder="Email address"
                  value={signer.email}
                  onChange={e => updateSigner(i, 'email', e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={() => removeSigner(i)}
                disabled={signers.length <= 1}
                className="mt-2 text-red-400 hover:text-red-600 disabled:opacity-30"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addSigner}
          className="mt-3 flex items-center gap-1 text-blue-600 text-sm hover:underline"
        >
          <Plus className="w-4 h-4" /> Add another signer
        </button>
      </div>

      {/* Options */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h2 className="font-semibold text-gray-800 mb-4">Signing Options</h2>
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={sequential}
            onChange={e => setSequential(e.target.checked)}
            className="w-4 h-4 accent-blue-600"
          />
          <div>
            <span className="text-sm font-medium text-gray-700">Sequential signing</span>
            <p className="text-xs text-gray-400">Signers receive invitations in order (1, then 2, then 3...)</p>
          </div>
        </label>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message to signers <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            placeholder="Please review and sign this document at your earliest convenience..."
          />
        </div>
      </div>

      <button
        onClick={send}
        disabled={sending}
        className="w-full flex items-center justify-center gap-2 bg-green-600 text-white py-3 rounded-xl font-medium text-lg hover:bg-green-700 disabled:opacity-60 transition-colors"
      >
        {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        Send to {signers.length} Signer{signers.length > 1 ? 's' : ''}
      </button>
    </div>
  );
}
